<script setup>
  import {
    computed,
    onMounted,
    ref,
  } from 'vue';

  import img from '@js/image';

  import CredereHeader from '@/components/Credere/Header.vue';

  const props = defineProps({
    variant: {
      type: String,
      required: false,
      default: 'A',
      validator(value) {
        return ['A', 'B'].includes(value);
      },
    },
    handles: {
      type: Array,
      required: false, // do not set a default value!
    },
  });

  const refSocials = ref();

  const IMAGES = {
    A: `url(${img('showroom/banners/socials/A-left.webp')}), url(${img('showroom/banners/socials/A-right.webp')})`,
    B: `url(${img('showroom/banners/socials/B-left.webp')}), url(${img('showroom/banners/socials/B-right.webp')})`,
  };

  const SOCIALS = {
    facebook: { prefix: 'https://facebook.com/', icon: 'facebook-f', text: 'Facebook' },
    instagram: { prefix: 'https://instagram.com/', icon: 'instagram', text: 'Instagram' },
    tiktok: { prefix: 'https://tiktok.com/@', icon: 'tiktok', text: 'TikTok' },
  };

  const { layout, variants } = window.CREDERE;
  const _enabled = computed(() => layout?._enable_social_banner);
  const _variant = computed(() => variants?.social_banner || props.variant);
  const _handles = computed(() => (props.handles || layout?._social_banner?.handles || [])
    .filter((social) => SOCIALS[social.name] && social.handle)
    .map((social) => Object.assign( // eslint-disable-line prefer-object-spread
      {},
      social,
      { url: `${SOCIALS[social.name].prefix}${social.handle}` },
    )));

  onMounted(() => {
    if (!/^#/.test(_enabled.value)) {
      return;
    }

    const to = document.getElementById(_enabled.value.replace(/^#/, ''));

    if (to && refSocials.value) {
      to.append(refSocials.value);
    }
  });

  const classes = computed(() => ([
    'credere-vehicles-showroom-social-banner',
    `variant--${_variant.value}`,
  ]));
</script>

<template>
  <section
    v-if="_handles.length"
    ref="refSocials"
    :class="classes"
    :style="{ backgroundImage: IMAGES[_variant] }"
  >
    <credere-header
      class="credere-vehicles-showroom-social-banner--header"
      title="Siga nossas redes!"
      subtitle="Fique por dentro das novidades da nossa loja"
      size="larger"
    />

    <ul class="credere-vehicles-showroom-social-banner--list">
      <li
        v-for="social in _handles"
        :key="social.name"
        class="credere-vehicles-showroom-social-banner--item"
      >
        <a
          :href="social.url"
          class="credere-vehicles-showroom-social-banner--link"
          target="_blank"
          >
          <font-awesome-icon
          :icon="['fa-brands', SOCIALS[social.name].icon]"
          class="credere-vehicles-showroom-social-banner--icon"
          />

          <span class="credere-vehicles-showroom-social-banner--name">
            {{ SOCIALS[social.name].text }}
          </span>
        </a>
      </li>
    </ul>
  </section>
</template>

<style lang="scss">
  .credere-vehicles-showroom-social-banner {
    --bw: var(--token-border-width-heavy);

    --background-color: var(--token-color-brand-primary);
    --box-shadow: inset 0 0 0 var(--bw) rgba(0, 0, 0, .10);
    --border-radius: var(--token-border-radius-decorative);
    --color: var(--token-color-brand-contrast, var(--token-color-constant-light));
    --margin: var(--token-spacing-xxblaster) auto 0;
    --padding: var(--token-spacing-blaster);

    background-position: left bottom, right top;
    background-repeat: no-repeat;
    background-size: 22% auto, 22% auto;
    text-align: center;

    @container (max-width: 768px) {
      --padding: var(--token-spacing-giga) var(--token-spacing-s);

      background-position: -30% bottom, 130% top;
      background-size: 42% auto, 42% auto;
    }

    &.variant--B {
      --bw: var(--token-border-width-soft);

      --background-color: var(--token-color-constant-light);
      --color: var(--token-color-constant-dark);

      background-position: left bottom, right top;
      background-size: auto 100%;

      @container (max-width: 768px) {
        background-position: -18% bottom, 118% top;
      }
    }
  }

  .credere-vehicles-showroom-social-banner--header {
    --margin: 0 auto var(--token-spacing-giga);

    @container (max-width: 768px) {
      --margin: 0 auto var(--token-spacing-giga);
    }

    .credere-header-subtitle {
      --color: inherit;
      --margin: var(--token-spacing-xxxs) auto 0;

      max-width: 50%;
    }
  }

  .credere-vehicles-showroom-social-banner--list {
    --gap: var(--token-spacing-xxxs);

    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    list-style: none;

    .variant--B & {
      --font-size: var(--token-font-size-xl);
    }
  }

  .credere-vehicles-showroom-social-banner--link {
    --border-radius: var(--token-border-radius-small);
    --background-color: rgba(0, 0, 0, .2);
    --line-height: 1;
    --padding: var(--token-spacing-xs);

    display: block;

    .variant--B & {
      --background-color: transparent;
    }
  }

  .credere-vehicles-showroom-social-banner--icon {
    --color: inherit;
    --margin: -0.4em 0 0 0;
    --font-size: 1.4em;

    transform: translateY(0.1em);
  }

  .credere-vehicles-showroom-social-banner--name {
    --margin: 0 0 0 var(--token-spacing-micro);

    display: inline-block;

    .variant--B & {
      display: none;
    }

    @container (max-width: 768px) {
      display: none;
    }
  }
</style>
